<template lang="pug">
v-container.pa-8(fluid)
  //- Page header
  v-row.mb-2(no-gutters align="center")
    v-col.shrink
      v-btn.ml-n3(text icon @click="goBack")
        v-icon(color="neutral700" :size="24") mdi-arrow-left
    v-col.ml-3
      .text-2xl.font-semibold.neutral700--text e-Invoice

  //- Tabs and actions row
  v-row.pb-2.pb-md-0(no-gutters align="center")
    v-col(cols="12" md="7")
      yz-tabs(v-model="activeTab" @change="onTabChange")
        yz-tab(label="All")
        yz-tab(label="Draft")
        yz-tab(label="Submitted")
        yz-tab(label="Valid")
        yz-tab(label="Invalid")
        yz-tab(label="Credit Note")
        yz-tab(label="Debit Note")

    v-col(cols="12" md="5")
      .d-flex.align-center.justify-end
        yz-search-btn(v-model="searchText" @search="onSearch" size="lg")
        v-btn.ml-2(icon @click="openColumnSettings")
          v-icon(size="20" color="neutral500") $yzi-settings-02
        yz-btn.ml-3(label="Create consolidated e-Invoice" color="primary" size="lg" @click="createConsolidatedEInvoice")

  //- Filter row
  v-row.my-4(no-gutters align="center")
    v-col
      filter-row(:filters.sync="filters")

  //- Content area
  .content-area
    //- Empty state
    yz-empty-state-card.mt-8(v-if="!isLoading && items.length === 0" icon="$yzi-file-06" title="No data available" description="Created e-Invoices will show up here")

    //- Data table (when data exists)
    .data-table(v-else)
      yz-data-table.mt-4(:headers="headers" :items="items" item-key="id" :items-per-page="pageSize" :loading="isLoading" :disable-sort="isMobileScreen" @click:row="viewItem")
        template(v-slot:no-data)
          .mt-16
            v-img.mx-auto(src="@/assets/images/icon_empty.png" width="80")
            .mt-2.p2.basic600--text No data available

        //- Date column
        template(v-slot:item.date="{ item }")
          div.yz-regular-body-sm.neutral700--text {{ item.date | humanizeDate }}

        //- Type column
        template(v-slot:item.type="{ item }")
          div.yz-regular-body-sm.neutral700--text {{ item.type | capitalize }}

        //- Invoices column
        template(v-slot:item.invoices="{ item }")
          div.yz-regular-body-sm.neutral700--text {{ item.invoices ? `${item.invoices} ${item.invoices === 1 ? 'invoice' : 'invoices'}` : '-' }}

        //- Total column
        template(v-slot:item.total="{ item }")
          div.yz-medium-body-sm.neutral700--text {{ item.total | displayPrice($currency) }}

        //- Status column
        template(v-slot:item.status="{ item }")
          y-chip.yz-regular-body-sm(:label="item.status_text" :color="getStatusColor(item.status)")
            template(#prepend)
              v-icon.mr-1(size="12" :color="getStatusIconColor(item.status)") mdi-record

        //- Actions column
        template(v-slot:item.actions="{ item, index }")
          div(@click.stop)
            v-menu(offset-y :top="menuPositionTop(index)" left attach rounded="lg")
              template(#activator="{ on }")
                v-btn(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list.py-0
                v-list-item(@click="viewItem(item)")
                  v-list-item-title.text-sm.basic800--text View details
                template(v-if="item.status === 'invalid'")
                  v-divider
                  v-list-item(@click="viewItem(item)")
                    v-list-item-title.text-sm.basic800--text Resubmit e-Invoice
                template(v-if="item.status === 'valid' && item.credit_note_required")
                  v-divider
                  v-list-item(@click="createCreditNote(item)")
                    v-list-item-title.text-sm.basic800--text Create credit note
                template(v-if="item.status === 'valid' && item.debit_note_required")
                  v-divider
                  v-list-item(@click="createDebitNote(item)")
                    v-list-item-title.text-sm.basic800--text Create debit note
                template(v-if="item.status === 'draft'")
                  v-divider
                  v-list-item(@click="deleteItem(item)")
                    v-list-item-title.text-sm.danger--text Delete

  //- Pagination
  .d-flex.align-center.mt-4(v-if="totalCount > pageSize")
    v-spacer
    yz-pagination(v-model="currentPage" :page-size="pageSize" :count="totalCount" type="page" arrow @input="onPageChange")

  //- Create Dialog (select invoices)
  create-consolidated-e-invoice-dialog(v-if="showCreateDialog" @confirm="onDialogConfirm" @closed="onDialogClosed")

  //- Consolidate Dialog (fullscreen review & submit)
  consolidate-e-invoice-dialog(v-if="showConsolidateDialog" v-bind="consolidateConfig" @back="onConsolidateBack" @save="onConsolidateSave" @submit="onConsolidateSubmit" @close="onConsolidateClose" @create-note="onCreateNoteFromDialog")

  //- Credit/Debit Note Dialog
  credit-debit-note-dialog(v-if="showCreditDebitDialog" :open.sync="showCreditDebitDialog" :einvoice-id="selectedNoteEinvoiceId" :note-type="selectedNoteType" @cancel="onCreditDebitClose" @close="onCreditDebitClose" @submit="onCreditDebitSubmit")

  //- Delete Confirmation Dialog
  yz-delete-confirmation-dialog(v-model="showDeleteDialog" item="e-Invoice" :loading="isDeleting" @confirmed="onConfirmDelete" @canceled="showDeleteDialog = false")

  //- e-Invoice Setup Required Dialog
  yz-dialog(v-model="showSetupDialog" persistent scrollable max-width="480")
    yz-card
      yz-dialog-normal-header(title="e-Invoice Setup Required" disable-close)
      yz-dialog-normal-content.pa-6
        .yz-regular-body-sm.neutral700--text Your e-Invoice settings have not been configured yet. Please set up your e-Invoice details before proceeding.
      yz-dialog-normal-actions(cancel-label="Go Back" save-label="Go to Settings" @cancel="goBack" @save="$router.push('/admin/clinic/settings/e-invoice')")
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import FilterRow from '@/components/clinic/einvoice/filter/FilterRow.vue'
import CreateConsolidatedEInvoiceDialog from '@/components/clinic/einvoice/CreateConsolidatedEInvoiceDialog.vue'
import ConsolidateEInvoiceDialog from '@/components/clinic/einvoice/ConsolidateEInvoiceDialog.vue'
import CreditDebitNoteDialog from '@/components/clinic/einvoice/CreditDebitNoteDialog.vue'

export default {
  name: 'ConsolidatedEInvoice',
  components: {
    FilterRow,
    CreateConsolidatedEInvoiceDialog,
    ConsolidateEInvoiceDialog,
    CreditDebitNoteDialog
  },
  data: () => ({
    isLoading: false,
    activeTab: 0,
    searchText: '',
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    items: [],
    showCreateDialog: false,
    showConsolidateDialog: false,
    consolidateConfig: null,
    showCreditDebitDialog: false,
    selectedNoteType: 'debit',
    selectedNoteEinvoiceId: null,
    showDeleteDialog: false,
    isDeleting: false,
    deleteTargetItem: null,
    showSetupDialog: false,

    // Filters from filter row component
    filters: [],

    // Table headers
    headers: [
      { text: 'DATE', value: 'date', class: 'text-table-header' },
      { text: 'TYPE', value: 'type', class: 'text-table-header' },
      { text: 'INVOICES', value: 'invoices', class: 'text-table-header' },
      { text: 'TOTAL', value: 'total', class: 'text-table-header' },
      { text: 'STATUS', value: 'status', class: 'text-table-header' },
      { text: '', value: 'actions', class: 'text-table-header', sortable: false, width: '64' },
    ],
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    isMobileScreen() {
      return this.$vuetify.breakpoint.xs
    },
    tabStatusMap() {
      return {
        0: null, // All
        1: { key: 'status', value: 'draft' },
        2: { key: 'status', value: 'submitted' },
        3: { key: 'status', value: 'valid' },
        4: { key: 'status', value: 'invalid' },
        5: { key: 'adjustment_required', value: 'credit' },
        6: { key: 'adjustment_required', value: 'debit' },
      }
    },
  },
  watch: {
    filters: {
      handler() {
        this.fetchFirstPage()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchMyinvois()
    this.fetchData()
  },
  methods: {
    ...mapActions(['showMessage']),

    fetchData() {
      this.isLoading = true
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
        company: this.company?.id,
      }

      // Tab filter
      const tabFilter = this.tabStatusMap[this.activeTab]
      if (tabFilter) {
        params[tabFilter.key] = tabFilter.value
      }

      // Search filter
      if (this.searchText) {
        params.search = this.searchText
      }

      // Apply filters from filter row component
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })

      this.$axios.get('/einvoices', { params })
        .then(resp => {
          this.items = resp.data.results.map(item => ({
            id: item.id,
            date: item.start_date || item.created,
            type: item.type,
            invoices: item.invoice_count,
            total: item.total_amount,
            status: item.status,
            status_text: item.status_text,
            credit_note_required: item.credit_note_required,
            debit_note_required: item.debit_note_required,
          }))
          this.totalCount = resp.data.count
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response?.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    fetchMyinvois() {
      this.$axios.get(`/companies/${this.company.slug}/myinvois`)
        .catch(err => {
          if (err.response && err.response.status === 404) {
            this.showSetupDialog = true
          }
        })
    },

    fetchFirstPage() {
      this.currentPage = 1
      this.fetchData()
    },

    onTabChange() {
      this.searchText = ''
      this.fetchFirstPage()
    },

    onSearch: debounce(function() {
      this.fetchFirstPage()
    }, 750),

    onPageChange() {
      this.fetchData()
    },

    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case 'draft':
          return 'normal'
        case 'submitted':
          return 'blue'
        case 'resubmitted':
          return 'orange'
        case 'valid':
          return 'green'
        case 'invalid':
          return 'red'
        default:
          return 'normal'
      }
    },

    getStatusIconColor(status) {
      switch (status?.toLowerCase()) {
        case 'draft':
          return 'neutral500'
        case 'submitted':
          return 'info500'
        case 'resubmitted':
          return 'warning500'
        case 'valid':
          return 'success500'
        case 'invalid':
          return 'danger500'
        default:
          return 'neutral500'
      }
    },

    viewItem(item) {
      this.consolidateConfig = { einvoiceId: item.id }
      this.showConsolidateDialog = true
    },

    deleteItem(item) {
      this.deleteTargetItem = item
      this.showDeleteDialog = true
    },

    onConfirmDelete() {
      this.isDeleting = true
      this.$axios.delete(`/einvoices/${this.deleteTargetItem.id}/`)
        .then(() => {
          this.showMessage(['success', 'e-Invoice deleted successfully'])
          this.showDeleteDialog = false
          this.fetchData()
        })
        .catch(err => {
          const message = err.response?.data?.detail || this.$t('error_message.unable_to_complete_request')
          this.showMessage(['error', message])
        })
        .finally(() => { this.isDeleting = false, this.deleteTargetItem = null })
    },

    createCreditNote(item) {
      this.selectedNoteType = 'credit'
      this.selectedNoteEinvoiceId = item.id
      this.showCreditDebitDialog = true
    },

    createDebitNote(item) {
      this.selectedNoteType = 'debit'
      this.selectedNoteEinvoiceId = item.id
      this.showCreditDebitDialog = true
    },

    onCreateNoteFromDialog({ type, einvoiceId }) {
      this.closeConsolidateDialog()
      this.selectedNoteType = type
      this.selectedNoteEinvoiceId = einvoiceId
      this.showCreditDebitDialog = true
    },

    onCreditDebitClose() {
      this.showCreditDebitDialog = false
      this.selectedNoteEinvoiceId = null
    },

    onCreditDebitSubmit() {
      this.showCreditDebitDialog = false
      this.selectedNoteEinvoiceId = null
      this.fetchData()
    },

    createConsolidatedEInvoice() {
      this.showCreateDialog = true
    },

    onDialogConfirm({ paymentMethod, dateFilter, panelData }) {
      this.showCreateDialog = false
      this.consolidateConfig = { paymentMethod, dateFilter: dateFilter || null, panelData: panelData || null }
      this.showConsolidateDialog = true
    },

    onDialogClosed() {
      this.showCreateDialog = false
    },

    closeConsolidateDialog() {
      this.showConsolidateDialog = false
      this.consolidateConfig = null
    },

    onConsolidateBack() {
      this.closeConsolidateDialog()
      this.showCreateDialog = true
    },

    onConsolidateSave() {
      this.showMessage(['success', 'Consolidated e-Invoice saved as draft'])
      this.closeConsolidateDialog()
      this.fetchData()
    },

    onConsolidateSubmit() {
      this.showMessage(['success', 'Consolidated e-Invoice submitted successfully'])
      this.closeConsolidateDialog()
      this.fetchData()
    },

    onConsolidateClose() {
      this.closeConsolidateDialog()
    },

    openColumnSettings() {
      // TODO: Implement column customization
    },

    menuPositionTop(index) {
      let position = index + 1
      if (position > 7) {
        let start = this.items.length - 3
        if (position > start) {
          return true
        }
      }
      return false
    },

    goBack() {
      this.$router.go(-1)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .yz-data-table tbody tr:hover {
  cursor: pointer;
}

.content-area {
  min-height: 300px;
}
</style>
